import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
// @ts-ignore
import { Button } from '@netbasequid/core'

import { useSysTheme, useDisplayTheme } from 'hooks/useUiState'
import { SystemTheme } from 'hooks/useUiState/types'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  getDashboardInfos,
  getRecentViewedDashboardInfos
} from 'features/dashboardInfo/asyncThunks'
import {
  selectDashboardInfoStatus,
  selectCategoryIsTemplate,
  selectDashboardInfoCategory
} from 'features/dashboardInfo/selectors'
import { CATEGORY } from 'features/dashboardInfo/constants'
import { DashboardCategory } from 'features/dashboardInfo/types'
import { AutomationId } from 'utils/automationUtil'
import openHelpPage from 'utils/helpLinkUtil'

const CenterCaontainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0px 24px;
`

const WordingContainer = styled.span<{ $theme: SystemTheme }>`
  margin: 8px 0px;
  text-align: center;
  color: ${({ $theme }) => $theme.text.placeholder};
  font-size: ${({ $theme }) => $theme.typescale[14].size};
  line-height: ${({ $theme }) => $theme.typescale[14].lineHeight};
  font-weight: ${({ $theme }) => $theme.fontWeight.regular};
`

const ClickableText = styled.span<{ $theme: SystemTheme }>`
  cursor: pointer;
  color: ${({ $theme }) => $theme.interaction.primary.default};
  text-decoration: underline;
  &:hover {
    color: ${({ $theme }) => $theme.interaction.primary.hover};
  }
  &:active {
    color: ${({ $theme }) => $theme.interaction.primary.pressed};
  }
`

const NoDashboardsFound = () => {
  const theme = useSysTheme()
  const mode = useDisplayTheme()
  const { t } = useTranslation()
  const categoryIsTemplate = useAppSelector(selectCategoryIsTemplate)
  const category = useAppSelector(selectDashboardInfoCategory)
  const hasLearnMore = ([
    CATEGORY.companyDashboards,
    CATEGORY.myTemplates,
    CATEGORY.companyTemplates
  ] as DashboardCategory[]).includes(category)

  return (
    <CenterCaontainer>
      <img
        data-testid={AutomationId.sidePannel.noDataFoundImg}
        src={`${process.env.PUBLIC_URL}/assets/no-dashboard-${mode}.png`}
        alt="no dashboard"
      />
      <WordingContainer data-testid={AutomationId.sidePannel.noDataFound} $theme={theme}>
        {categoryIsTemplate ? t('noTemplatesFound') : t('noDashboardsFound')}
        {hasLearnMore && (
          <>
            &nbsp;
            <ClickableText
              $theme={theme}
              onClick={() => openHelpPage(category === CATEGORY.myTemplates ? 8243202 : 9824734)}>
              {t('learnMore')}
            </ClickableText>
          </>
        )}
      </WordingContainer>
    </CenterCaontainer>
  )
}

const UnknownError = () => {
  const theme = useSysTheme()
  const mode = useDisplayTheme()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const category = useAppSelector(selectDashboardInfoCategory)

  const handleRefreshClick = () => {
    if (category === CATEGORY.recentlyViewedDashboards) {
      dispatch(getRecentViewedDashboardInfos({ staleTime: 0 }))
    } else {
      dispatch(getDashboardInfos({ category, options: { staleTime: 0 } }))
    }
  }
  return (
    <CenterCaontainer>
      <img
        src={`${process.env.PUBLIC_URL}/assets/dashboard-error-${mode}.png`}
        alt="dashboard error"
      />
      <WordingContainer $theme={theme}>{t('sidebarUnknownError')}</WordingContainer>
      <Button mode={mode} onClick={handleRefreshClick}>
        {t('refresh')}
      </Button>
    </CenterCaontainer>
  )
}

const ErrorHandle = () => {
  const status = useAppSelector(selectDashboardInfoStatus)
  return status === 'failed' ? <UnknownError /> : <NoDashboardsFound />
}

export default ErrorHandle
